import {
  useMoveOwnerMediaMutation,
  useUploadOwnerMediaFileMutation,
} from "@/services/media";
import { MediaFileType } from "@/constants/base";
import { useAppDispatch } from "@app/hooks";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldNotification } from "@emerald-react/notification";
import { useSurvey } from "@hooks/useSurveySelectors";
import type { PropertyMediaGroup } from "@survey-types/survey.media.types";
import { removeMedia, updateMedia } from "@survey/surveySlice";
import { checkForActiveContent } from "@shared/utils/fileValidation";
import React, { useRef, useState } from "react";
import AttachmentUploads from "./AttachmentUploads";

interface FileGalleryProps {
  propertyId: string;
  propertyMedia: PropertyMediaGroup | null;
}

const FileGallery = ({ propertyId, propertyMedia }: FileGalleryProps) => {
  const [errorBool, setError] = useState(false);
  const [failedFiles, setFailedFiles] = useState<
    Array<{ fileName: string; errorMsg: string }>
  >([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadOwnerMediaFile] = useUploadOwnerMediaFileMutation();
  const [moveOwnerMedia] = useMoveOwnerMediaMutation();
  const currentSurvey = useSurvey();
  const dispatch = useAppDispatch();
  const useremail = localStorage.getItem("email");

  const images = propertyMedia?.images || [];
  const floorplans = propertyMedia?.floorPlans || [];
  const fliers = propertyMedia?.fliers || [];

  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/jpg",
    "image/avif",
    "image/av1",
  ];
  const validPdfTypes = ["application/pdf"];
  const allAcceptedTypes = [...validImageTypes, ...validPdfTypes];
  const maxPdfFileSizeBytes = 52428800; // 50 MB

  const setUploadError = (msg: string, fileName?: string) => {
    setError(true);
    setFailedFiles((prev) => [
      ...prev,
      { fileName: fileName ?? "Unknown file", errorMsg: msg },
    ]);
  };

  const checkForValidDocument = async (
    file: Blob,
    fileName?: string,
  ): Promise<boolean> => {
    try {
      const buffer = await file.arrayBuffer();
      const text = new TextDecoder("iso-8859-1").decode(buffer);
      if (
        text.includes("Encrypt") ||
        text
          .substring(text.lastIndexOf("<<"), text.lastIndexOf(">>"))
          .includes("/Encrypt")
      ) {
        setUploadError(
          "The file(s) you are attempting to upload is (are) either encrypted or password protected and cannot be included in a Site IQ Survey. Please disable password protection and/or the encryption and re-upload to Site IQ.",
          fileName,
        );
        return false;
      }
      const hasActiveContent = await checkForActiveContent(file);
      if (hasActiveContent) {
        setUploadError(
          "This PDF contains embedded scripts or active content and cannot be uploaded. Please use a standard PDF without JavaScript or embedded files.",
          fileName,
        );
        return false;
      }
      return true;
    } catch {
      setUploadError("Failed to read the file.", fileName);
      return false;
    }
  };

  const checkForValidFile = async (file: File): Promise<boolean> => {
    const isPdf = file.type === "application/pdf";

    if (isPdf && file.size > maxPdfFileSizeBytes) {
      setUploadError(
        "The PDF file exceeds the 50 MB size limit. Please upload a smaller file.",
        file.name,
      );
      return false;
    }

    if (isPdf) {
      return checkForValidDocument(
        new Blob([file], { type: "application/pdf" }),
        file.name,
      );
    }

    // Validate image types
    const isValidImageExt = !!file.name
      .toLowerCase()
      .match(/\.(png|jpeg|gif|jpg|avif|av1)$/);
    const isValidImageMime = validImageTypes.includes(file.type);
    if (!isValidImageExt || !isValidImageMime) {
      setUploadError(
        "The file type is not supported. Please upload JPEG, PNG, AVIF, or PDF files.",
        file.name,
      );
      return false;
    }
    return true;
  };

  const processFiles = (files: File[]) => {
    setError(false);
    setFailedFiles([]);
    files.forEach(async (file) => {
      const isValid = await checkForValidFile(file);
      if (!isValid) return;

      // Route PDFs to Flyers by default; images to Images by default
      const isPdf = file.type === "application/pdf";
      const targetFileType: "images" | "floorPlans" | "fliers" = isPdf
        ? "fliers"
        : "images";
      const targetMediaType = targetFileType.toLocaleUpperCase();

      const allFilesInTarget = isPdf ? fliers : images;

      const formData = new FormData();
      formData.append("file", file, file.name.replace(/[^a-zA-Z0-9.\-_ ]/g, "_"));
      formData.append(
        "media",
        JSON.stringify({
          surveyId: currentSurvey?.id,
          user: useremail,
          propertyId,
          mediaType: targetMediaType,
        }),
      );

      const tempMediaId = "temp-id-" + crypto.randomUUID();
      dispatch(
        updateMedia({
          propertyId,
          mediaType: targetFileType,
          media: [
            {
              mediaId: tempMediaId,
              displayName: file.name,
              mediaType: targetFileType,
              propertyId,
              cloudfrontUrl: "",
              isHidden: false,
              surveyId: currentSurvey?.id || "",
              order: (allFilesInTarget.length || 0).toString(),
            },
          ],
        }),
      );

      uploadOwnerMediaFile(formData)
        .unwrap()
        .then((result) => {
          dispatch(
            removeMedia({ propertyId, mediaType: targetFileType, mediaId: tempMediaId }),
          );
          dispatch(
            updateMedia({ propertyId, mediaType: targetFileType, media: [result] }),
          );
        })
        .catch((error) => {
          setUploadError(
            "File upload failed. " + (error.data?.error || ""),
            file.name,
          );
          dispatch(
            removeMedia({ propertyId, mediaType: targetFileType, mediaId: tempMediaId }),
          );
          console.error("Error uploading file:", error);
        });
    });
  };

  // Drag-and-drop handlers for the custom upload zone
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only clear when leaving the zone itself, not a child element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) processFiles(files);
  };

  // Hidden file-input change (Browse File button)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) processFiles(files);
    // Reset so the same file can be re-selected
    e.target.value = "";
  };

  // Keyboard paste (Ctrl+V / Cmd+V) on the upload zone
  const handlePasteEvent = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.clipboardData.items);
    const files = items
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length > 0) processFiles(files);
  };

  // "Paste From Clipboard" button — uses async Clipboard API
  const handlePasteFromClipboard = async () => {
    if (!navigator.clipboard?.read) {
      setUploadError(
        "Clipboard access is not supported in this browser. Try pressing Ctrl+V / Cmd+V while the upload area is focused.",
        "Clipboard",
      );
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      const files: File[] = [];
      for (const item of items) {
        for (const mimeType of item.types) {
          if (validImageTypes.includes(mimeType)) {
            const blob = await item.getType(mimeType);
            const ext = mimeType.split("/")[1] ?? "png";
            files.push(new File([blob], `clipboard-image.${ext}`, { type: mimeType }));
          }
        }
      }
      if (files.length === 0) {
        setUploadError(
          "Clipboard Paste Error: The file name or path cannot be copied from the clipboard. Please open the image itself, copy to your clipboard, and try again.",
          "Clipboard",
        );
        return;
      }
      processFiles(files);
    } catch {
      setUploadError(
        "Could not read clipboard. Please allow clipboard access in your browser settings, or drag & drop / browse for the file.",
        "Clipboard",
      );
    }
  };

  const handleMoveFile = (    mediaId: string,
    fromType: "images" | "floorPlans" | "fliers",
    toType: "images" | "floorPlans" | "fliers",
  ) => {
    if (!currentSurvey?.id) return;
    moveOwnerMedia({
      surveyId: currentSurvey.id,
      propertyId,
      mediaId,
      fromType,
      toType,
    }).catch((error) => {
      console.error("Error moving file:", error);
    });
  };

  return (
    <div className="file-gallery-container grid grid-flow-row gap-6">
      {/* Unified upload zone */}
      <div>
        <p className="mb-3 text-sm text-gray-600">
          Add attachments into the designated area below. Upload from your
          computer, drag and drop files, or paste an image from your clipboard.
        </p>

        {/* Custom dashed drop zone */}
        <div
          role="region"
          aria-label="File upload area"
          tabIndex={0}
          className={[
            "flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-8 py-8 text-center transition-colors outline-none",
            "focus-visible:ring-2 focus-visible:ring-offset-2",
            isDragOver
              ? "border-green-600 bg-green-50"
              : "border-gray-300 bg-gray-50 hover:border-green-500",
          ].join(" ")}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onPaste={handlePasteEvent}
        >
          <p className="font-semibold text-sm text-gray-800">
            Browse files, drag &amp; drop, or paste an image from your clipboard
          </p>
          <p className="text-xs text-gray-400">
            JPEG, PNG, AVIF and PDF formats, up to 50MB.
          </p>
          <div className="flex items-center gap-3">
            <EmeraldButton
              type="Secondary"
              size="Medium"
              onClick={() => fileInputRef.current?.click()}
            >
              Browse File
            </EmeraldButton>
            <span className="text-sm text-gray-400">or</span>
            <EmeraldButton
              type="Primary"
              size="Medium"
              onClick={handlePasteFromClipboard}
            >
              Paste From Clipboard
            </EmeraldButton>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allAcceptedTypes.join(",")}
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      {errorBool && (
        <div style={{ width: "100%", display: "block" }}>
          <EmeraldNotification
            variant="error"
            title="The following file/s failed"
            message={failedFiles
              .map((file) => {
                const lower = file.errorMsg.toLowerCase();
                const isEncrypted =
                  lower.includes("encrypted") ||
                  lower.includes("password protected");
                const isSize =
                  lower.includes("50 mb") || lower.includes("size limit");
              const isClipboardError = file.errorMsg.startsWith("Clipboard Paste Error:");
              const display = isEncrypted
                ? file.errorMsg
                : isSize
                  ? "File size must be 50 MB or less."
                  : file.errorMsg.includes("not supported")
                    ? "File type is not supported"
                    : file.errorMsg;
              return isClipboardError
                ? `• ${display}`
                : `• File: ${file.fileName}  Reason: ${display}`;
              })
              .join("\n")}
            closable
            onClose={() => {
              setError(false);
              setFailedFiles([]);
            }}
            style={{ width: "100%", maxWidth: "none", boxSizing: "border-box", whiteSpace: "pre-line" }}
          />
        </div>
      )}

      {showInfoBanner && (
        <div style={{ width: "100%", display: "block" }}>
          <EmeraldNotification
            variant="info"
            message={`By default jpg/jpeg, png and avif files will upload to Images, pdf files will upload to ${currentSurvey?.flyerLabel || "Flyers"}. Once uploaded, you can reassign to ${currentSurvey?.sitePlanLabel || "Site Plans"} as needed.`}
            closable
            onClose={() => setShowInfoBanner(false)}
            style={{ width: "100%", maxWidth: "none", boxSizing: "border-box" }}
          />
        </div>
      )}

      {/* Images section */}
      <h2 className="text-xl text-data">Images</h2>
      {images.length === 0 ? (
        <p className="text-sm text-gray-500">You have no images attached.</p>
      ) : (
        <AttachmentUploads
          fileType={MediaFileType.images}
          propertyId={propertyId}
          mediaFiles={images}
          showUploadZone={false}
          onMoveFile={handleMoveFile}
        />
      )}

      {/* Site Plans section */}
      <hr className="border-t border-gray-200" />
      <h2 className="text-xl text-data">{currentSurvey?.sitePlanLabel || "Site Plans"}</h2>
      {floorplans.length === 0 ? (
        <p className="text-sm text-gray-500">You have no {(currentSurvey?.sitePlanLabel || "site plans").toLowerCase()} attached.</p>
      ) : (
        <AttachmentUploads
          fileType={MediaFileType.floorplans}
          propertyId={propertyId}
          mediaFiles={floorplans}
          showUploadZone={false}
          onMoveFile={handleMoveFile}
        />
      )}

      {/* Flyers section */}
      <hr className="border-t border-gray-200" />
      <h2 className="text-xl text-data">{currentSurvey?.flyerLabel || "Flyers"}</h2>
      {fliers.length === 0 ? (
        <p className="text-sm text-gray-500">You have no {(currentSurvey?.flyerLabel || "flyers").toLowerCase()} attached.</p>
      ) : (
        <AttachmentUploads
          fileType={MediaFileType.fliers}
          propertyId={propertyId}
          mediaFiles={fliers}
          showUploadZone={false}
          onMoveFile={handleMoveFile}
        />
      )}
      <hr className="border-t border-gray-200" />
    </div>
  );
};

export default FileGallery;
